import { AssetDetailsViewQuery$data } from '@bts-web/data-layer/server';

export type CurrencyValue = NonNullable<
  NonNullable<AssetDetailsViewQuery$data['assetById']>['prices']
>[number];

/**
 * Gets typical precision for common currencies
 * @param currencyCode - The currency symbol (USD, EUR, etc.)
 * @returns Typical precision for the currency
 */
function getTypicalCurrencyPrecision(currencyCode: string): number {
  const typicalPrecisions: Record<string, number> = {
    // Major fiat currencies - typically 2 decimal places
    USD: 2,
    EUR: 2,
    GBP: 2,
    CAD: 2,
    AUD: 2,
    CHF: 2,
    SEK: 2,
    NOK: 2,
    DKK: 2,
    PLN: 2,
    CZK: 2,
    HUF: 2,
    BGN: 2,
    RON: 2,
    HRK: 2,
    RSD: 2,
    TRY: 2,
    RUB: 2,

    // Asian currencies
    JPY: 0,
    KRW: 0,
    VND: 0, // No decimal places
    CNY: 2,
    HKD: 2,
    SGD: 2,
    TWD: 2,
    THB: 2,
    MYR: 2,
    PHP: 2,
    IDR: 0,
    INR: 2,

    // Middle East & Africa
    AED: 2,
    SAR: 2,
    QAR: 2,
    KWD: 3,
    BHD: 3,
    OMR: 3,
    EGP: 2,
    ZAR: 2,
    NGN: 2,

    // Latin America
    BRL: 2,
    MXN: 2,
    ARS: 2,
    CLP: 0,
    COP: 2,
    PEN: 2,

    // Cryptocurrencies - typically more precision
    BTC: 8,
    ETH: 6,
    LTC: 8,
    XRP: 6,
    ADA: 6,
    DOT: 4,
    USDT: 2,
    USDC: 2,
    BUSD: 2,
    DAI: 2,
  };

  return typicalPrecisions[currencyCode.toUpperCase()] ?? 2; // Default to 2 if unknown
}

/**
 * Formats a single currency value using Intl.NumberFormat
 * @param currencyData - The currency data object
 * @param locale - Optional locale (defaults to 'en-US')
 * @returns Formatted currency string like "108,853.83 USD"
 */
function formatCurrency(currencyData: CurrencyValue, locale = 'en-US'): string {
  // Handle undefined/null input gracefully
  if (!currencyData) {
    return '0.00 N/A';
  }

  const { fiat, value } = currencyData;

  // Handle missing value
  if (!value) {
    return '0.00 N/A';
  }

  const numericValue = parseFloat(value.value);

  if (isNaN(numericValue)) {
    return '0.00 N/A';
  }

  // Determine precision: use data precision, or fallback to typical currency precision
  let precision = value.precision;

  // Check for null/undefined precision, but allow 0
  if (precision === undefined || precision === null) {
    if (fiat?.symbol) {
      precision = getTypicalCurrencyPrecision(fiat.symbol);
    } else {
      precision = 2; // Default fallback
    }
  }

  // Ensure precision is a valid number (but don't override 0)
  precision = Math.max(0, Math.min(20, precision));

  // Create formatter with the determined precision
  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });

  const formattedNumber = formatter.format(numericValue);

  return `${formattedNumber} ${fiat?.symbol || 'N/A'}`;
}

/**
 * Formats multiple currency values
 * @param currencyArray - Array of currency data objects
 * @param locale - Optional locale (defaults to 'en-US')
 * @returns Array of formatted currency strings
 */
function formatCurrencies(
  currencyArray: CurrencyValue[],
  locale = 'en-US',
): string[] {
  return currencyArray.map((currencyData) =>
    formatCurrency(currencyData, locale),
  );
}

/**
 * Alternative version that uses the currency code for proper currency formatting
 * This will add currency symbols (like $ for USD) automatically
 * @param currencyData - The currency data object
 * @param locale - Optional locale (defaults to 'en-US')
 * @returns Formatted currency string like "$108,853.83"
 */
function formatCurrencyWithSymbol(
  currencyData: CurrencyValue,
  locale = 'en-US',
): string {
  // Handle undefined/null input gracefully
  if (!currencyData) {
    return '$0.00';
  }

  const { fiat, value } = currencyData;

  // Handle missing value
  if (!value) {
    return '$0.00';
  }

  const numericValue = parseFloat(value.value);

  if (isNaN(numericValue)) {
    return '$0.00';
  }

  // Determine precision: use data precision, or fallback to typical currency precision
  let precision = value.precision;

  // Check for null/undefined precision, but allow 0
  if (precision === undefined || precision === null) {
    if (fiat?.symbol) {
      precision = getTypicalCurrencyPrecision(fiat.symbol);
    } else {
      precision = 2; // Default fallback
    }
  }

  // Ensure precision is a valid number (but don't override 0)
  precision = Math.max(0, Math.min(20, precision));

  // List of currencies that are known to work well with Intl.NumberFormat
  const wellSupportedCurrencies = new Set([
    'USD',
    'EUR',
    'GBP',
    'JPY',
    'CAD',
    'AUD',
    'CHF',
    'CNY',
    'SEK',
    'NOK',
    'DKK',
    'PLN',
    'CZK',
    'HUF',
    'BGN',
    'RON',
    'HRK',
    'RSD',
    'TRY',
    'RUB',
    'HKD',
    'SGD',
    'TWD',
    'THB',
    'MYR',
    'PHP',
    'IDR',
    'INR',
    'AED',
    'SAR',
    'QAR',
    'KWD',
    'BHD',
    'OMR',
    'EGP',
    'ZAR',
    'NGN',
    'BRL',
    'MXN',
    'ARS',
    'CLP',
    'COP',
    'PEN',
  ]);

  // Determine which currency to use: provided currency or USD fallback
  const currencyToUse = fiat?.symbol || 'USD';

  // If currency is not well supported, fall back to basic format immediately
  // Exception: if fiat is null, we want to use USD formatting, not basic format
  if (fiat && !wellSupportedCurrencies.has(currencyToUse.toUpperCase())) {
    // Create adjusted currency data with the calculated precision for consistent fallback
    const adjustedCurrencyData: CurrencyValue = {
      ...currencyData,
      value: {
        ...value,
        precision: precision,
      },
    };

    return formatCurrency(adjustedCurrencyData, locale);
  }

  // Create formatter with currency style
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyToUse,
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });

  try {
    return formatter.format(numericValue);
  } catch (_error) {
    // Fallback if currency code is not recognized
    console.warn(
      `Currency code ${currencyToUse} not recognized, falling back to basic format`,
    );

    // Create adjusted currency data with the calculated precision for consistent fallback
    const adjustedCurrencyData: CurrencyValue = {
      ...currencyData,
      value: {
        ...value,
        precision: precision,
      },
    };

    return formatCurrency(adjustedCurrencyData, locale);
  }
}

/**
 * Smart currency formatter that handles edge cases and provides sensible defaults
 * @param currencyData - The currency data object
 * @param locale - Optional locale (defaults to 'en-US')
 * @param options - Optional formatting options
 * @returns Formatted currency string
 */
function formatCurrencySmart(
  currencyData: CurrencyValue,
  locale = 'en-US',
  options: {
    useSymbol?: boolean; // Use currency symbol ($) vs text (USD)
    maxPrecision?: number; // Override max precision
    minPrecision?: number; // Override min precision
  } = {},
): string {
  // Handle undefined/null input gracefully
  if (!currencyData) {
    return '0.00 N/A';
  }

  const { fiat, value } = currencyData;

  // Handle missing value
  if (!value) {
    return '0.00 N/A';
  }

  const numericValue = parseFloat(value.value);

  if (isNaN(numericValue)) {
    return `0.00 ${fiat?.symbol || 'N/A'}`;
  }

  // Determine precision
  let precision = value.precision;

  // Check for null/undefined precision, but allow 0
  if (precision === undefined || precision === null) {
    if (fiat?.symbol) {
      precision = getTypicalCurrencyPrecision(fiat.symbol);
    } else {
      precision = 2; // Default fallback
    }
  }

  // Apply options overrides
  if (options.maxPrecision !== undefined) {
    precision = Math.min(precision, options.maxPrecision);
  }

  if (options.minPrecision !== undefined) {
    precision = Math.max(precision, options.minPrecision);
  }

  // Ensure precision is valid (but don't override 0)
  precision = Math.max(0, Math.min(20, precision));

  // Create a modified currency data object with the adjusted precision
  const adjustedCurrencyData: CurrencyValue = {
    ...currencyData,
    value: {
      ...value,
      precision: precision,
    },
  };

  if (options.useSymbol && fiat?.symbol) {
    return formatCurrencyWithSymbol(adjustedCurrencyData, locale);
  } else {
    return formatCurrency(adjustedCurrencyData, locale);
  }
}

export {
  formatCurrency,
  formatCurrencies,
  formatCurrencyWithSymbol,
  formatCurrencySmart,
  getTypicalCurrencyPrecision,
};
